import React, {
  useCallback,
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react'
import io from 'socket.io-client'
import {
  getFallbackTypeFromAction,
  parseWebpushChangedObjects,
} from './webpushPayload'

const WebpushContext = createContext({
  object: null,
  setObject: () => {},
  clockingLoading: false,
  setClockingLoading: () => {},
})

export function WebpushProvider({ children }) {
  const [object, setCurrentObject] = useState(null)
  const [clockingLoading, setClockingLoading] = useState(false)
  const objectQueueRef = useRef([])
  const dispatchingObjectRef = useRef(false)
  const dispatchTimeoutRef = useRef(null)

  const setObject = useCallback(nextObject => {
    if (!nextObject) return

    objectQueueRef.current.push(nextObject)
    if (dispatchingObjectRef.current) return

    dispatchingObjectRef.current = true
    setCurrentObject(objectQueueRef.current.shift())
  }, [])

  useEffect(() => {
    if (!object) return undefined

    dispatchTimeoutRef.current = window.setTimeout(() => {
      const nextObject = objectQueueRef.current.shift()
      if (nextObject) {
        setCurrentObject(nextObject)
        return
      }

      dispatchingObjectRef.current = false
    }, 0)

    return () => {
      if (dispatchTimeoutRef.current) {
        window.clearTimeout(dispatchTimeoutRef.current)
        dispatchTimeoutRef.current = null
      }
    }
  }, [object])

  const value = useMemo(
    () => ({
      object,
      setObject,
      clockingLoading,
      setClockingLoading,
    }),
    [object, clockingLoading]
  )

  return (
    <WebpushContext.Provider value={value}>{children}</WebpushContext.Provider>
  )
}

export function useWebpush() {
  return useContext(WebpushContext)
}

const shouldEmitAction = action => {
  const normalized = String(action || '').toLowerCase()
  return [
    'clocking.start',
    'clocking.stop',
    'clocking.update',
    'clocking.updated',
    'clocking.delete',
    'clocking.deleted',
    'status.update',
    'status.updated',
    'status.delete',
    'status.deleted',
    'status.release',
    'status.released',
    'status.accept',
    'status.accepted',
    'timeentry.create',
    'timeentry.created',
    'timeentry.update',
    'timeentry.updated',
    'timeentry.delete',
    'timeentry.deleted',
    'timeslot.create',
    'timeslot.created',
    'timeslot.update',
    'timeslot.updated',
    'timeslot.delete',
    'timeslot.deleted',
    'timeentry.release',
    'timeentry.released',
    'timeentry.accept',
    'timeentry.accepted',
    'timeslot.release',
    'timeslot.released',
    'timeslot.accept',
    'timeslot.accepted',
  ].includes(normalized)
}

export function Socket({ delegateUserId }) {
  const socketRef = useRef(null)
  const eventSeqRef = useRef(0)
  const { setObject } = useWebpush()

  useEffect(() => {
    const userStr = localStorage.getItem('user')
    const webpush_url = localStorage.getItem('webpush_url')

    if (!userStr || !webpush_url) {
      return
    }

    const user = JSON.parse(userStr)
    const socketUserId = delegateUserId || user?.username
    if (!socketUserId) {
      return
    }

    const socket = io(webpush_url, {
      query: { userId: socketUserId },
      transports: ['websocket'],
    })

    socketRef.current = socket

    // socket.on('connect', () => {
    //     console.log(' [Webpush] socket connected', socket.id);
    // });

    // socket.on('connect_error', (err) => {
    //     console.error(' [Webpush] connect_error:', err);
    // });

    socket.on('broad', message => {
      const action = message?.payload?.action
      if (message?.signal !== true || !action || !shouldEmitAction(action))
        return

      const changedObjects = parseWebpushChangedObjects(
        action,
        message.payload.item
      )

      if (!changedObjects.length) {
        eventSeqRef.current += 1
        setObject({
          action,
          type: getFallbackTypeFromAction(action),
          id: null,
          seq: eventSeqRef.current,
        })
        return
      }

      changedObjects.forEach(changedObject => {
        eventSeqRef.current += 1
        setObject({
          action,
          ...changedObject,
          seq: eventSeqRef.current,
        })
      })
    })

    // socket.on('disconnect', (reason) => {
    //     console.log('[Webpush] disconnected:', reason);
    // });

    return () => {
      // console.log(' [Webpush] cleanup disconnect');
      socket.disconnect()
      if (socketRef.current === socket) {
        socketRef.current = null
      }
    }
  }, [delegateUserId, setObject])

  return null
}

export function WebpushListener({ onChange }) {
  const { object } = useWebpush()
  const lastRef = useRef(null)

  useEffect(() => {
    if (!object || object === lastRef.current) return
    lastRef.current = object
    onChange?.(object)
  }, [object])

  return null
}
